<template>
	<div id="widget-container"
		 :class="[`pr overflow-hidden`, {'flex justify-center': !widgetLoaded, modal: widget.modal}, $css(styles.container)]">
	</div>
</template>

<script>
	import {mapGetters, mapState, mapActions} from "vuex";
	import config from "@/common/config";
	import consts from "@/common/consts";
	import {getInMediaQuery as gimq, MQ_SIZES} from "@/utils/cssUtils";
	import {types as appTypes} from "@/store/modules/app";

	const URL_CONFIG_PTRN = /config=([\w:,.]*)&?$/;

	const TYPES = {
		MODAL: "MODAL",
		INLINE: "INLINE",
	};

	let widgets = self.__widgets = {
		[TYPES.MODAL]: null,
		[TYPES.INLINE]: null,
	};

	const getConfigFonts = (state) => ({
		...consts.FONTS,
		[state.font]: {
			active: true,
			url: consts.FONTS[state.font],
		}
	});

	const getStateConfig = (state, isDev, modal) => {

		return {
			cloudName: state.cloudName ||
				(isDev ? config.widget.defaultDevCloud : config.widget.defaultCloud),

			uploadPreset: state.uploadPreset ||
				(isDev ? config.widget.defaultDevPreset : config.widget.defaultPreset),

			sources: state.sources,

			styles: {
				palette: state.colors,
				fonts: getConfigFonts(state),
				frame: modal ? {background: "transparent"} : undefined,
			},

			...state.advanced,
		};
	};

	const getPositionParams = (state, modal) => ({
		inlineContainer: modal ? undefined : "#widget-container",
		frameContainer: modal ? "#widget-container" : undefined,
		frameZIndex: modal ? 10000 : undefined,
		queueViewPosition: modal ? "right:2px" : undefined,
	});

	const getUrlConfig = () => {
		let params;

		try {
			const m = URL_CONFIG_PTRN.exec(document.location.search);

			if (m && m[1]) {
				params = m[1].split(",")
					.reduce((res, p) => {
						const kv = p.split(":"),
							num = parseFloat(kv[1]); //naive try to turn numeric param into number

						res[kv[0]] = (num ? num : kv[1]);

						return res;
					},{});

				console.debug(`[Demo]: loaded config params from config: `, params);
			}
		}
		catch (e) {
			console.debug(`[Demo]: failed to parse url config params`, e);
		}

		return params;
	};

	const getWidgetConfig = (state, modal) => {
		const isDev = !!~document.location.search.indexOf("dev"),
			isStaging = !!~document.location.search.indexOf("staging");

		return {
			dev: isDev,
			staging: isStaging,
			uploadPrefix: isStaging ? config.widget.stagingUploadPrefix : undefined,
			googleApiKey: config.widget.googleApiKey,
			secureDistribution: isDev ? "res-dev.cloudinary.com" : isStaging ? "res-staging.cloudinary.com" : undefined,
			secure: true,
			folder: config.widget.folder,
			searchByRights: true,
			multiple: true,
			thumbnails: "#thumbnails-container",
			...getPositionParams(state, modal),
			...getStateConfig(state, isDev, modal),
			...getUrlConfig(),
		};
	};

	const createWidgetByType = (type, state, widgetCallback) => {
		widgets[type] = self.cloudinary.createUploadWidget(
			getWidgetConfig(state, (type === TYPES.MODAL)),
			widgetCallback);
	};

	const openWidget = (state, widgetCallback) => {
		if (self.cloudinary) {

			const immediateType = state.modal ? TYPES.MODAL : TYPES.INLINE,
				delayType = state.modal ? TYPES.INLINE : TYPES.MODAL;

			createWidgetByType(immediateType, state, widgetCallback);

			if (state.modalSupported) {
				setTimeout(() =>
					createWidgetByType(delayType, state, widgetCallback), 800); //delay a little so we load faster the type thats going to show first
			}

			showWidget(state.modal);
		}
		else {
			setTimeout(openWidget.bind(null, state, widgetCallback), 200);
		}
	};

	const showWidget = (modal) => {
		[
			[TYPES.MODAL, (modal ? "open" : "hide")],
			[TYPES.INLINE, (modal ? "hide" : "open")],
		]
			.forEach((conf) => {
				if (widgets[conf[0]]) {
					widgets[conf[0]][conf[1]]();
				}
			});
	};

	const updateWidget = (config) =>
		Object.keys(TYPES).forEach((t) => widgets[t] && widgets[t].update(config));

	const monitorStateChange = (store, selector, cb, deep = false) =>
		store.watch(typeof selector === "function" ? selector : (state) => state.widget[selector],
			cb, {deep});

	const monitorSourcesChange = (store) =>
		monitorStateChange(store, "activeSources", (sources) =>
			updateWidget({sources}));

	const monitorColorsChange = (store) =>
		monitorStateChange(store, "colors", (colors) =>
			updateWidget({styles: {palette: colors}}));

	const monitorFontsChange = (store) =>
		monitorStateChange(store, "font", (font) =>
			updateWidget({styles: {fonts: {[font]: {active: true}}}}));

	const monitorAdvancedChange = (store) =>
		monitorStateChange(store, "advanced", (advanced) =>
			updateWidget({...advanced}));

	const monitorCloudPreset = (store) =>
		monitorStateChange(store,
			(state) => ({
				cloudName: state.widget.cloudName,
				uploadPreset: state.widget.uploadPreset
			}), (cloudPreset) =>
				updateWidget({...cloudPreset}));

	const monitorModalChange = (store) =>
		monitorStateChange(store, "modal", showWidget);

	function styles() {
		return this.$style({
			container: gimq(
				[MQ_SIZES.W, {minHeight: "none"}],
				[MQ_SIZES.M, {width: "100%", marginTop: 0}]),
			containerModal: this.widget.modal ? gimq(
				[MQ_SIZES.S, {marginBottom: "140px"}],
				[MQ_SIZES.W, {minHeight: "840px"}]) : {},
			tabButton: gimq(MQ_SIZES.S, {padding: "18px 5px"}),
		});
	}

	export default {
		name: "Widget",

		computed: {
			...mapGetters("widget", {widget: "getWidgetState"}),
			...mapState({
				widgetLoaded: (state) => state.app.widgetLoaded,
			}),
			styles,
		},

		mounted() {
			openWidget(this.widget,
				this.widgetCallback.bind(this));

			const store = this.$store;

			monitorSourcesChange(store);
			monitorColorsChange(store);
			monitorFontsChange(store);
			monitorAdvancedChange(store);
			monitorCloudPreset(store);
			monitorModalChange(store);
		},

		methods: {
			...mapActions("app", [appTypes.SET_WIDGET_LOADED]),

			"source-changed"() {
				if (!this.widgetLoaded) {
					console.log("[Demo]: Inline widget loaded ");
					this[appTypes.SET_WIDGET_LOADED]();
				}
			},

			handleUwEvent(type, info) {
				if (this[type]) {
					this[type](info);
				}
			},

			widgetCallback(err, info) {
				if (err) {
					console.error("[Demo]: UW reported an error", err);
				}
				else if (info) {
					if (info.data && info.uw_event) {
						this.handleUwEvent(info.data.event, info.data.info);
					}
				}
			},
		},
	}
</script>

<style lang="scss" scoped>

	#widget-container {
		width: 98%;
		max-width: 790px;

		&.modal {
			height: 100%;
			width: 100%;
			max-width: 100%;
			margin: 0 0 240px;
		}
	}
</style>
